// ============================================================================
// Font presets — config-driven typography
// ----------------------------------------------------------------------------
// config.theme.font selects a preset key from FONT_PRESETS (falls back to the
// default). ThemeProvider calls applyFontPreset() which sets the --font-*
// CSS variables and injects the matching Google Fonts stylesheet.
// Add a new preset here to offer another typeface option to clients.
// ============================================================================

export interface FontPreset {
  /** Display font for headings, prices, business name */
  heading: string
  /** Body font */
  body: string
  /** Font used under [dir="rtl"] (Arabic) */
  arabic: string
  /** The `family=...` query for fonts.googleapis.com/css2 (without the base) */
  google: string
}

export const FONT_PRESETS: Record<string, FontPreset> = {
  fraunces: {
    heading: '"Fraunces", Georgia, serif',
    body: '"Hanken Grotesk", system-ui, sans-serif',
    arabic: '"Cairo", "Hanken Grotesk", sans-serif',
    google:
      'family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Hanken+Grotesk:wght@400;500;600;700&family=Cairo:wght@400;500;600;700',
  },
  poppins: {
    heading: '"Poppins", system-ui, sans-serif',
    body: '"Poppins", system-ui, sans-serif',
    arabic: '"Cairo", "Poppins", sans-serif',
    google: 'family=Poppins:wght@400;500;600;700&family=Cairo:wght@400;500;600;700',
  },
  playfair: {
    heading: '"Playfair Display", Georgia, serif',
    body: '"Source Sans 3", system-ui, sans-serif',
    arabic: '"Cairo", sans-serif',
    google:
      'family=Playfair+Display:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=Cairo:wght@400;500;600;700',
  },
  inter: {
    heading: '"Inter", system-ui, sans-serif',
    body: '"Inter", system-ui, sans-serif',
    arabic: '"Cairo", sans-serif',
    google: 'family=Inter:wght@400;500;600;700&family=Cairo:wght@400;500;600;700',
  },
  dm: {
    heading: '"DM Serif Display", Georgia, serif',
    body: '"DM Sans", system-ui, sans-serif',
    arabic: '"Cairo", sans-serif',
    google:
      'family=DM+Serif+Display:wght@400&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Cairo:wght@400;500;600;700',
  },
}

export const DEFAULT_FONT_KEY = 'fraunces'
const FONT_LINK_ID = 'omnirise-fonts'
const SESSION_DEFAULT_LINK_ID = 'omnirise-default-fonts'

export function getFontPreset(key?: string): FontPreset {
  if (key && FONT_PRESETS[key]) return FONT_PRESETS[key]
  return FONT_PRESETS[DEFAULT_FONT_KEY]
}

/**
 * Apply a font preset: set the --font-* CSS variables and (for non-default
 * presets) inject the Google Fonts stylesheet. The default preset is also
 * loaded via a static <link> in index.html to avoid a flash of unstyled text.
 */
export function applyFontPreset(key?: string): FontPreset {
  const resolvedKey = key && FONT_PRESETS[key] ? key : DEFAULT_FONT_KEY
  const preset = FONT_PRESETS[resolvedKey]
  const root = document.documentElement

  root.style.setProperty('--font-heading', preset.heading)
  root.style.setProperty('--font-body', preset.body)
  root.style.setProperty('--font-arabic', preset.arabic)

  // Only inject a dynamic stylesheet when a non-default preset is chosen;
  // the default is already linked statically in index.html (no FOUC).
  if (resolvedKey !== DEFAULT_FONT_KEY) {
    let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = FONT_LINK_ID
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = `https://fonts.googleapis.com/css2?${preset.google}&display=swap`
    // The static default link is now redundant; disable it so the active
    // preset's metrics win.
    const staticDefault = document.getElementById(
      SESSION_DEFAULT_LINK_ID
    ) as HTMLLinkElement | null
    if (staticDefault) staticDefault.disabled = true
  }

  return preset
}
